<script setup lang="ts">
import type { Incident } from '@/types';
import { statusLocale, statusMessages } from '@/i18n';

defineProps<{
  incidents: Incident[];
  heading: string;
  showEmptyState: boolean;
}>();

const impactClass: Record<string, string> = {
  minor: 'tone-warn',
  major: 'tone-warn-strong',
  critical: 'tone-bad',
  maintenance: 'tone-muted',
};

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString(statusLocale, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
</script>

<template>
  <section class="incident-section">
    <h2 class="incident-heading">{{ heading }}</h2>

    <p v-if="incidents.length === 0 && showEmptyState" class="incident-empty">
      {{ statusMessages.noRecentIncidents }}
    </p>

    <article
      v-for="incident in incidents"
      :key="incident.id"
      class="incident-card"
      :class="impactClass[incident.impact] ?? 'tone-muted'"
    >
      <header class="incident-header">
        <div>
          <h3>{{ incident.title }}</h3>
          <p class="incident-meta">
            <span class="incident-impact">{{ incident.impact }}</span>
            <span class="incident-time">{{ statusMessages.startedAt(formatDate(incident.startedAt)) }}</span>
            <a
              v-if="incident.ticketUrl"
              :href="incident.ticketUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="incident-ticket"
            >
              {{ statusMessages.viewTicket }}
            </a>
          </p>
        </div>
        <span class="incident-status">{{ incident.status }}</span>
      </header>

      <ol class="incident-updates">
        <li v-for="(update, idx) in incident.updates" :key="idx">
          <time>{{ formatDate(update.timestamp) }}</time>
          <strong>{{ update.status }}</strong>
          <p>{{ update.message }}</p>
        </li>
      </ol>
    </article>
  </section>
</template>

<style scoped>
.incident-section {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.incident-heading {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--xt-fg-strong);
}

.incident-empty {
  margin: 0;
  padding: 1rem 1.25rem;
  border: 1px dashed var(--xt-border);
  border-radius: 0.5rem;
  color: var(--xt-fg-muted);
  font-size: 0.9rem;
}

.incident-card {
  border: 1px solid var(--xt-border);
  border-left: 3px solid var(--xt-tone-fg);
  border-radius: 0.5rem;
  background: var(--xt-bg-elevated);
  padding: 1rem 1.25rem;
}

.incident-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 0.75rem;
}

.incident-header h3 {
  margin: 0 0 0.25rem;
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--xt-fg-strong);
}

.incident-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin: 0;
  font-size: 0.8rem;
  color: var(--xt-fg-muted);
}

.incident-impact {
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-weight: 600;
  color: var(--xt-tone-fg);
}

.incident-ticket {
  color: var(--xt-fg-link);
  text-decoration: none;
}

.incident-ticket:hover {
  text-decoration: underline;
}

.incident-status {
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-weight: 600;
  color: var(--xt-tone-fg);
}

.incident-updates {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.incident-updates li {
  padding: 0.5rem 0.75rem;
  background: var(--xt-bg-base);
  border-radius: 0.375rem;
  font-size: 0.85rem;
}

.incident-updates time {
  display: inline-block;
  margin-right: 0.5rem;
  color: var(--xt-fg-muted);
}

.incident-updates strong {
  text-transform: uppercase;
  font-size: 0.75rem;
  letter-spacing: 0.05em;
  margin-right: 0.5rem;
  color: var(--xt-tone-fg);
}

.incident-updates p {
  margin: 0.25rem 0 0;
  color: var(--xt-fg-strong);
}
</style>
